import { useTranslation } from "react-i18next";
import { Button, NumberInput, Stack, Textarea, Group, TextInput, MultiSelect, Select, Text } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";

import { useUpdateMySession } from "@hooks/mutations/onboarding/useUpdateMySession";
import { CustomPhoneInput } from "@components/CustomPhoneInput";
import { SpecialtyMultiInput } from "@components/SpecialtyMultiInput";
import { usePrimarySpecializations, primarySpecializationOptions } from "@hooks/queries/profile/usePrimarySpecializations";

const schema = z.object({
  bio: z.string().min(10, "Bio must be at least 10 characters").optional().or(z.literal("")),
  primary_specialization_id: z.string().min(1, "Select a specialization"),
  years_of_experience: z.number().min(0, "Invalid experience").optional().nullable(),
  specialties: z.array(z.object({ value: z.string(), label: z.string() })).min(1, "Select at least one specialty").optional(),
  location: z.string().min(2, "Location is required").optional().or(z.literal("")),
  languages: z.array(z.string()).min(1, "At least one language is required").optional(),
  consultation_modes: z.array(z.string()).min(1, "Select at least one consultation mode").optional(),
  license_number: z.string().optional().or(z.literal("")),
  licensed_countries: z.array(z.string()).min(1, "Select at least one country or Worldwide").optional(),
  payout_method: z.string().optional().nullable(),
  bank_name: z.string().optional().or(z.literal("")),
  bank_account_number: z.string().optional().or(z.literal("")),
  mobile_money_number: z.string().optional().or(z.literal("")),
  mobile_money_provider: z.string().optional().or(z.literal("")),
  mobile_money_country_code: z.string().length(2).optional().or(z.literal("")),
});

type Inputs = z.infer<typeof schema>;

interface Step3Props {
  sessionData: any;
}

const formatPhone = (val?: string) => {
  if (!val) return val;
  // Strip any spaces or formatting just in case, but usually CustomPhoneInput handles it
  const cleanVal = val.replace(/\s+/g, "");
  return cleanVal.startsWith("+") ? cleanVal : `+${cleanVal}`;
};

export const Step3ProfileDraft = ({ sessionData }: Step3Props) => {
  const { t } = useTranslation();
  const { data: primarySpecializations } = usePrimarySpecializations();
  const { mutate: updateSession, isLoading: isSaving } = useUpdateMySession();

  const LANGUAGE_OPTIONS = [
    { value: "English", label: "English" },
    { value: "French", label: "French" },
    { value: "Spanish", label: "Spanish" },
    { value: "Arabic", label: "Arabic" },
    { value: "Portuguese", label: "Portuguese" },
  ];

  const CONSULTATION_MODE_OPTIONS = [
    { value: "teleconsultation", label: "Teleconsultation" },
    { value: "homecare", label: "Homecare" },
  ];

  const COUNTRY_OPTIONS = [
    { value: "WW", label: "Worldwide (e.g. Paramedics)" },
    { value: "CI", label: "Côte d'Ivoire (CI)" },
    { value: "US", label: "United States (US)" },
    { value: "FR", label: "France (FR)" },
    { value: "GB", label: "United Kingdom (GB)" },
    { value: "CA", label: "Canada (CA)" },
  ];

  const PAYOUT_METHOD_OPTIONS = [
    { value: "bank_transfer", label: t("onboarding-bank-transfer") },
    { value: "mobile_money", label: t("onboarding-mobile-money") },
  ];

  const MOBILE_MONEY_PROVIDERS = [
    { value: "WAVE", label: "Wave" },
    { value: "ORANGE", label: "Orange Money" },
    { value: "MTN", label: "MTN Mobile Money" },
    { value: "MOOV", label: "Moov Money" },
  ];

  const form = useForm<Inputs>({
    validate: zodResolver(schema),
    initialValues: {
      bio: sessionData.bio || "",
      primary_specialization_id: sessionData.primary_specialization_id || "",
      years_of_experience: sessionData.years_of_experience || null,
      specialties: sessionData.specialties || [],
      location: sessionData.location || "",
      languages: sessionData.languages || [],
      consultation_modes: sessionData.consultation_modes || [],
      license_number: sessionData.license_number || "",
      licensed_countries: sessionData.licensed_countries || [],
      payout_method: sessionData.payout_method || null,
      bank_name: sessionData.bank_name || "",
      bank_account_number: sessionData.bank_account_number || "",
      mobile_money_number: sessionData.mobile_money_number || "",
      mobile_money_provider: sessionData.mobile_money_provider || "",
      mobile_money_country_code: sessionData.mobile_money_country_code || "",
    },
  });

  const payoutMethod = form.values.payout_method;

  const handleSaveAndContinue = (values: Inputs) => {
    updateSession({
      ...values,
      mobile_money_number: formatPhone(values.mobile_money_number),
      current_step: 4, // Advance to next step (Services)
    });
  };

  const handleSaveDraft = () => {
    const values = form.getValues();
    updateSession({
      ...values,
      mobile_money_number: formatPhone(values.mobile_money_number),
    });
  };

  const handleBack = () => {
    updateSession({ current_step: 2 });
  };

  return (
    <form onSubmit={form.onSubmit(handleSaveAndContinue)}>
      <Stack gap="lg">
        <Textarea
          label={t("onboarding-bio")}
          description={t("onboarding-bio-desc")}
          placeholder="I am a dedicated physician with a passion for..."
          minRows={5}
          withAsterisk
          key={form.key("bio")}
          {...form.getInputProps("bio")}
        />

        <Select searchable
          label={t("onboarding-title")}
          data={primarySpecializationOptions(primarySpecializations)}
          withAsterisk
          key={form.key("primary_specialization_id")}
          {...form.getInputProps("primary_specialization_id")}
        />

        <Group grow>
          <NumberInput
            label={t("onboarding-years-exp")}
            placeholder="e.g. 10"
            withAsterisk
            min={0}
            max={70}
            key={form.key("years_of_experience")}
            {...form.getInputProps("years_of_experience")}
          />
          <TextInput
            label={t("onboarding-license-number")}
            placeholder="e.g. MD12345678"
            key={form.key("license_number")}
            {...form.getInputProps("license_number")}
          />
        </Group>

        <Group grow>
          <TextInput
            label={t("onboarding-location")}
            placeholder="e.g. Abidjan, Ivory Coast"
            withAsterisk
            key={form.key("location")}
            {...form.getInputProps("location")}
          />
        </Group>

        <SpecialtyMultiInput
          label={t("onboarding-specialties")}
          placeholder="Search and add your specialties"
          withAsterisk
          required
          key={form.key("specialties")}
          value={form.getValues().specialties || []}
          onChange={(val) => form.setFieldValue("specialties", val)}
          error={form.errors.specialties as string}
        />

        <MultiSelect
          label={t("onboarding-licensed-countries")}
          description="Where are you legally allowed to practice? Select 'Worldwide' if you are a paramedic."
          placeholder="Select countries"
          data={COUNTRY_OPTIONS}
          withAsterisk
          searchable
          clearable
          key={form.key("licensed_countries")}
          {...form.getInputProps("licensed_countries")}
        />

        <Group grow>
          <MultiSelect
            label={t("onboarding-languages")}
            placeholder="Select languages"
            data={LANGUAGE_OPTIONS}
            withAsterisk
            searchable
            clearable
            key={form.key("languages")}
            {...form.getInputProps("languages")}
          />
          <MultiSelect
            label={t("onboarding-consultation-modes")}
            placeholder="Select modes"
            data={CONSULTATION_MODE_OPTIONS}
            withAsterisk
            key={form.key("consultation_modes")}
            {...form.getInputProps("consultation_modes")}
          />
        </Group>

        {/* Payment Preferences */}
        <Stack gap="xs" mt="md" style={{ background: "#f8f9fa", padding: "16px", borderRadius: "8px" }}>
          <Select
            label={t("onboarding-payout")}
            description="How would you like to receive your payments?"
            placeholder="Select payout method"
            data={PAYOUT_METHOD_OPTIONS}
            clearable
            key={form.key("payout_method")}
            {...form.getInputProps("payout_method")}
            onChange={(val) => form.setFieldValue("payout_method", val)}
          />

          {payoutMethod === "bank_transfer" && (
            <Group grow>
              <TextInput
                label={t("onboarding-bank-name")}
                placeholder="e.g. Ecobank"
                key={form.key("bank_name")}
                {...form.getInputProps("bank_name")}
              />
              <TextInput
                label={t("onboarding-account-number")}
                placeholder="e.g. CI123456789"
                key={form.key("bank_account_number")}
                {...form.getInputProps("bank_account_number")}
              />
            </Group>
          )}

          {payoutMethod === "mobile_money" && (
            <Group grow align="flex-start">
              <Select
                label="Mobile Money Provider"
                placeholder="Select provider"
                data={MOBILE_MONEY_PROVIDERS}
                withAsterisk
                key={form.key("mobile_money_provider")}
                {...form.getInputProps("mobile_money_provider")}
                onChange={(val) => form.setFieldValue("mobile_money_provider", val || undefined)}
              />
              <Stack gap={4}>
                <Text fw={500} size="sm" mt="sm">{t("onboarding-mobile-number")}</Text>
                <CustomPhoneInput
                  phone={form.getValues().mobile_money_number || ""}
                  setPhone={(val) => form.setFieldValue("mobile_money_number", val)}
                  setCountryCode={(val) => form.setFieldValue("mobile_money_country_code", val)}
                />
                {form.errors.mobile_money_number && <Text c="red" size="xs">{form.errors.mobile_money_number as string}</Text>}
              </Stack>
            </Group>
          )}
        </Stack>

        <Group justify="space-between" mt="xl">
          <Button variant="outline" onClick={handleBack} loading={isSaving} type="button">
            {t("onboarding-back")}
          </Button>
          <Group>
            <Button variant="outline" onClick={handleSaveDraft} loading={isSaving} type="button">
              {t("onboarding-save-draft")}
            </Button>
            <Button type="submit" loading={isSaving} size="md">
              {t("onboarding-save-continue")}
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};
